import { getNlsString } from '../../nls/nlsRuntime'
import type { NlsInterpolationValues } from '../../nls/nlsTypes'
import type { NlsKey } from '../../nls/strings_nls'
import type { FormField, FormFieldType, FormLifecycleStatus, FormRequestStatus, FormResponseStatus } from '../../types/forms'
import type { FormsSection } from './formsShellModel'

export type FormsNlsT = (key: NlsKey, values?: NlsInterpolationValues) => string

const defaultFormsT: FormsNlsT = (key, values) => getNlsString(key, values)

const SECTION_KEYS: Record<FormsSection, { label: NlsKey; tone: NlsKey }> = {
  HUB: { label: 'NLSFormsSectionHubLabel', tone: 'NLSFormsSectionHubTone' },
  TEMPLATES: { label: 'NLSFormsSectionTemplatesLabel', tone: 'NLSFormsSectionTemplatesTone' },
  DRAFTS: { label: 'NLSFormsSectionDraftsLabel', tone: 'NLSFormsSectionDraftsTone' },
  ARCHIVE: { label: 'NLSFormsSectionArchiveLabel', tone: 'NLSFormsSectionArchiveTone' },
  ASSIGN: { label: 'NLSFormsSectionAssignLabel', tone: 'NLSFormsSectionAssignTone' },
  ASSIGNED: { label: 'NLSFormsSectionAssignedLabel', tone: 'NLSFormsSectionAssignedTone' },
  RESPONSES: { label: 'NLSFormsSectionResponsesLabel', tone: 'NLSFormsSectionResponsesTone' },
}

export const getFormsSectionMeta = (section: FormsSection, t: FormsNlsT = defaultFormsT) => ({
  label: t(SECTION_KEYS[section].label),
  tone: t(SECTION_KEYS[section].tone),
})

export const getFormsDirtyNavigationDialog = (t: FormsNlsT = defaultFormsT) => ({
  title: t('NLSFormsDialogDirtyTitle'),
  message: t('NLSFormsDialogDirtyMessage'),
  cancelLabel: t('NLSCommonActionCancel'),
  discardLabel: t('NLSManageActionDiscard'),
  saveLabel: t('NLSManageActionSave'),
})

export const getFormsLifecycleStatusLabel = (status: FormLifecycleStatus | 'ALL', t: FormsNlsT = defaultFormsT): string => {
  if (status === 'ALL') return t('NLSFormsLabelAllStatuses')
  if (status === 'DRAFT') return t('NLSFormsStatusDraft')
  if (status === 'ACTIVE') return t('NLSFormsStatusActive')
  return t('NLSFormsStatusArchived')
}

export const getFormsRequestStatusLabel = (status: FormRequestStatus | 'ALL', t: FormsNlsT = defaultFormsT): string => {
  if (status === 'ALL') return t('NLSFormsLabelAll')
  if (status === 'PENDING') return t('NLSFormsStatusPending')
  if (status === 'COMPLETED') return t('NLSFormsStatusCompleted')
  if (status === 'CANCELLED') return t('NLSFormsStatusCancelled')
  return t('NLSFormsStatusExpired')
}

export const getFormsResponseStatusLabel = (status: FormResponseStatus | 'ALL', t: FormsNlsT = defaultFormsT): string => {
  if (status === 'ALL') return t('NLSFormsLabelAll')
  if (status === 'PENDING') return t('NLSFormsStatusPending')
  if (status === 'REVIEWED') return t('NLSFormsStatusReviewed')
  if (status === 'REJECTED') return t('NLSFormsStatusRejected')
  return t('NLSFormsStatusExported')
}

export const getFormsModeLabel = (mode: 'PREVIEW' | 'FILL' | 'RESPONSES', t: FormsNlsT = defaultFormsT): string => {
  if (mode === 'PREVIEW') return t('NLSFormsModePreview')
  if (mode === 'FILL') return t('NLSFormsModeFill')
  return t('NLSFormsModeResponses')
}

export const formatFormsResponseCount = (count: number, t: FormsNlsT = defaultFormsT): string =>
  t(count === 1 ? 'NLSFormsLabelResponsesOne' : 'NLSFormsLabelResponsesMany', { count })

export const getFormsFieldTypeLabel = (
  field: Pick<FormField, 'type'> & Partial<Pick<Extract<FormField, { type: 'TEXT' }>, 'multiline'>>,
  t: FormsNlsT = defaultFormsT,
): string => {
  if (field.type === 'TEXT' && field.multiline) return t('NLSFormsFieldTypeParagraph')
  return getFormsFieldTypeName(field.type, t)
}

export const getFormsFieldTypeName = (type: FormFieldType, t: FormsNlsT = defaultFormsT): string => {
  if (type === 'TEXT') return t('NLSFormsFieldTypeText')
  if (type === 'NUMBER') return t('NLSFormsFieldTypeNumber')
  if (type === 'DATE') return t('NLSFormsFieldTypeDate')
  if (type === 'DROPDOWN') return t('NLSFormsFieldTypeDropdown')
  if (type === 'CHECKBOX') return t('NLSFormsFieldTypeCheckbox')
  if (type === 'PERSON') return t('NLSFormsFieldTypePerson')
  return t('NLSFormsFieldTypeFileUpload')
}

export const getFormsPersonEmptyDirectoryLabel = (t: FormsNlsT = defaultFormsT): string =>
  t('NLSFormsPersonEmptyDirectory')

export const getFormsPersonDirectoryErrorLabel = (t: FormsNlsT = defaultFormsT): string =>
  t('NLSFormsPersonDirectoryError')

export const getFormsMarkReviewedLabel = (t: FormsNlsT = defaultFormsT): string =>
  t('NLSFormsActionMarkReviewed')

export const getFormsMarkExportedLabel = (t: FormsNlsT = defaultFormsT): string =>
  t('NLSFormsActionMarkExported')


